// 監控數據閾值配置
export interface ThresholdConfig {
  min?: number;
  max?: number;
  unit?: string;
}

export interface MonitoringThresholds {
  noise: ThresholdConfig;
  tsp: ThresholdConfig;
  vibration: ThresholdConfig;
}

// 預設閾值配置（可根據實際需求調整）
export const defaultThresholds: MonitoringThresholds = {
  noise: {
    min: 0,
    max: 75, // dB - 超過75dB可能異常
    unit: 'dB',
  },
  tsp: {
    min: 0,
    max: 20, // ug/m³ - 超過20可能異常
    unit: 'ug/m³',
  },
  vibration: {
    min: 0,
    max: 0.34, // mm/s - 超過0.34可能異常
    unit: 'mm/s',
  },
};

// 根據監控類型和值判斷狀態
export type MonitoringStatus = 'normal' | 'alert' | 'alarm';

// 根據 alertThreshold 創建動態閾值配置
export const createThresholdFromAlertThreshold = (
  alertThreshold: number | null | undefined,
  unit?: string
): ThresholdConfig | null => {
  if (alertThreshold === null || alertThreshold === undefined) {
    return null;
  }

  return {
    min: 0,
    max: alertThreshold, // max = alertThreshold
    unit: unit,
  };
};

// 檢查項目是否為 Grantham Hospital
export const isGranthamHospital = (projectName?: string | null): boolean => {
  if (!projectName) return false;
  return projectName.startsWith('葛量洪醫院') || projectName === 'Grantham Hospital';
};

export const getMonitoringStatus = (
  value: number,
  type: 'noise' | 'tsp' | 'vibration',
  thresholds: MonitoringThresholds = defaultThresholds,
  alertThreshold?: number | null | undefined,
  projectName?: string | null
): MonitoringStatus => {
  // 對於 Grantham Hospital 的 vibration，使用特殊的多級別閾值
  if (type === 'vibration' && isGranthamHospital(projectName)) {
    // Alert Level (yellow): > 0.25
    // Alarm Level (red): > 0.4
    if (value > 0.4) {
      return 'alarm'; // 紅色 - 原來的 alert 改名為 alarm
    }
    if (value > 0.34) {
      return 'alert'; // 黃色 - 新的 alert level
    }
    return 'normal';
  }

  if (type === 'tsp') {
    if (value > 500) {
      return 'alarm'; // 紅色
    }
    return 'normal';
  }

  // 如果提供了 alertThreshold，使用動態閾值
  let config: ThresholdConfig;
  if (alertThreshold !== null && alertThreshold !== undefined) {
    const dynamicConfig = createThresholdFromAlertThreshold(alertThreshold);
    if (dynamicConfig) {
      config = dynamicConfig;
    } else {
      config = thresholds[type];
    }
  } else {
    config = thresholds[type];
  }

  // 檢查是否超出最大/最小值（異常狀態）
  if (config.max !== undefined && value > config.max) {
    return 'alarm';
  }
  if (config.min !== undefined && value < config.min) {
    return 'alarm';
  }

  // 如果都不符合，返回正常狀態
  return 'normal';
};

